import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch/ListSearch'
import { WithLoader } from 'Components/WithLoader'
import RelatedSchools from 'Services/ControlDataManagement/Features/School/Components/RelatedSchools/RelatedSchools'
import SchoolDetails from 'Services/ControlDataManagement/Features/School/Components/SchoolDetails'
import SchoolList from 'Services/ControlDataManagement/Features/School/Components/SchoolList'
import { useSchoolsWithParams } from 'Services/ControlDataManagement/Features/School/Hooks'
import {
  useDeleteSingleSchoolMutation,
  useGetSingleSchoolQuery
} from 'Services/ControlDataManagement/Features/School/schoolApi'
import { SchoolTabType } from 'Services/ControlDataManagement/Features/School/types'
import { ROWS_PER_PAGE_DEFAULT, TAB_PARAM_KEY } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Divider, Tab, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

function SchoolListPage() {
  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const {
    schools,
    isFetching,
    searchQueryParams,
    setSearchQueryParams,
    totalResults,
    setSelectedSchoolId,
    selectedSchoolId
  } = useSchoolsWithParams()

  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromParams = searchParams.get(TAB_PARAM_KEY) ?? SchoolTabType.DETAILS

  const { data: singleSchool, isFetching: isSingleSchoolFetching } = useGetSingleSchoolQuery(
    selectedSchoolId ?? '',
    {
      skip: !selectedSchoolId
    }
  )

  const [deleteSingleSchool, { isLoading: isDeleteLoading }] = useDeleteSingleSchoolMutation()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedSchoolId)

  const breadcrumbs = [
    {
      title: t`Home`,
      link: '/control-data-management/'
    },
    {
      title: t`Schools`
    }
  ]

  const { searchText, handleChangePage, handleRequestSort, handleSearchTextChange, handleSearch } =
    useListFunctions({
      searchQueryParams,
      setSearchQueryParams
    })

  const onRowClick = (id: string) => {
    setSelectedSchoolId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedSchoolId('')
    searchParams.delete(TAB_PARAM_KEY)
    setSearchParams(searchParams)
  }

  const handleTabChange = (_event: React.SyntheticEvent | null, newValue: string) => {
    searchParams.set(TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleDelete = () => {
    if (!selectedSchoolId) return

    deleteSingleSchool(selectedSchoolId)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`School successfully deleted.`)
        handleDrawerClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the school.`)
      })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Schools`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate('/control-data-management/school-management/schools/create/')}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new school`}
            </CustomButton>
            <ListSearch
              searchText={searchText}
              handleSearchTextChange={handleSearchTextChange}
              handleSearch={handleSearch}
              placeholder={t`Search by name or code`}
            />
          </ListActions>
          <Box pt={1}>
            <SchoolList
              rows={schools}
              isLoading={isFetching || isDeleteLoading}
              onRowClick={onRowClick}
              selectedSchoolId={selectedSchoolId}
              params={searchQueryParams}
              handleRequestSort={handleRequestSort}
              handleChangePage={handleChangePage}
              count={totalResults}
              page={
                searchQueryParams.start_index
                  ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                  : 0
              }
            />
          </Box>
          <DrawerRight
            open={isDrawerOpen}
            onClose={handleDrawerClose}
            topBar={
              <Box>
                <DeleteButton
                  sx={{ mr: 1 }}
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the school?`,
                      handleConfirm: () => {
                        handleDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
                <CustomButton
                  onClick={() =>
                    navigate(
                      `/control-data-management/school-management/schools/${selectedSchoolId}/edit`
                    )
                  }
                  size="small"
                  startIcon={<EditIcon />}
                >{t`Edit`}</CustomButton>
              </Box>
            }
          >
            <WithLoader loading={isSingleSchoolFetching || isDeleteLoading}>
              {singleSchool && (
                <TabContext value={tabFromParams}>
                  <TabList aria-label={t`School tab list`} onChange={handleTabChange}>
                    <Tab label={t`Details`} value={SchoolTabType.DETAILS} />
                    <Tab label={t`Related units`} value={SchoolTabType.UNITS} />
                  </TabList>
                  <Divider />
                  <TabPanel sx={{ px: 0, pt: 0 }} value={SchoolTabType.DETAILS}>
                    <SchoolDetails school={singleSchool} />
                  </TabPanel>
                  <TabPanel sx={{ px: 0, pt: 0 }} value={SchoolTabType.UNITS}>
                    <RelatedSchools schoolId={singleSchool.school_id} />
                  </TabPanel>
                </TabContext>
              )}
            </WithLoader>
          </DrawerRight>
        </Box>
      </Box>
    </Box>
  )
}

export default SchoolListPage
